import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Redo as RedoIcon, Undo as UndoIcon } from "@mui/icons-material";
import { formatSummaryNumber, SelectionSummary } from "./selectionSummary";
import { CompiledView, SheetSource } from "./sheetTypes";
import { SheetViewState } from "./sheetViewState";

/**
 * What the view is doing right now, and how to undo any part of it.
 *
 * It renders only when the view differs from the file or something is selected,
 * so an untouched preview loses no height to it. Every chip removes exactly the
 * aspect it names - filters, sort, hidden columns and hidden rows are separate
 * things, and collapsing them into one "clear" would hide which is which.
 */

interface ViewStatusBarProps {
  source: SheetSource;
  view: CompiledView;
  state: SheetViewState;
  summary: SelectionSummary;
  selectionRows: number;
  selectionColumns: number;
  selectionKind: "cells" | "rows" | null;
  onClearFilter: (columnId: string) => void;
  onClearSort: () => void;
  onShowAllColumns: () => void;
  onShowAllRows: () => void;
  onUnfreeze: () => void;
  onResetColumnWidths: () => void;
  onResetColumnOrder: () => void;
  frozenCount: number;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onReset: () => void;
  onHideSelectedRows: () => void;
  onKeepOnlySelectedRows: () => void;
  onCopySelection: () => void;
  onClearSelection: () => void;
}

function columnTitle(source: SheetSource, columnId: string): string {
  const column = source.columns.find((item) => item.id === columnId);
  if (!column) return columnId;
  return column.label || column.letter;
}

function describeSort(source: SheetSource, state: SheetViewState): string {
  return state.sort
    .map((rule) => {
      const name = columnTitle(source, rule.columnId);
      return `${name} ${rule.direction === "asc" ? "A-Z" : "Z-A"}`;
    })
    .join(", then ");
}

const ViewStatusBar: React.FC<ViewStatusBarProps> = ({
  source,
  view,
  state,
  summary,
  selectionRows,
  selectionColumns,
  selectionKind,
  onClearFilter,
  onClearSort,
  onShowAllColumns,
  onShowAllRows,
  onUnfreeze,
  onResetColumnWidths,
  onResetColumnOrder,
  frozenCount,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onReset,
  onHideSelectedRows,
  onKeepOnlySelectedRows,
  onCopySelection,
  onClearSelection,
}) => {
  const filterIds = Object.keys(state.filters);
  const stats = view.stats;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: (t) => t.palette.grey[50],
        flexShrink: 0,
        overflowX: "auto",
        whiteSpace: "nowrap",
      }}
    >
      {selectionKind && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0 }}>
            {selectionKind === "rows"
              ? `${selectionRows.toLocaleString()} row${selectionRows === 1 ? "" : "s"} selected`
              : `${selectionRows.toLocaleString()} x ${selectionColumns.toLocaleString()} cells selected`}
          </Typography>
          <Button
            size="small"
            onClick={onHideSelectedRows}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Hide rows
          </Button>
          <Button
            size="small"
            onClick={onKeepOnlySelectedRows}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Keep only these
          </Button>
          <Button
            size="small"
            onClick={onCopySelection}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Copy
          </Button>
          <Button
            size="small"
            onClick={onClearSelection}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Clear
          </Button>
          {selectionKind === "cells" && summary.numeric > 0 && (
            <Tooltip
              title={`${summary.numeric} of ${summary.filled} selected cells read as numbers`}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0 }}
              >
                Count {summary.filled.toLocaleString()} - Sum{" "}
                {formatSummaryNumber(summary.sum ?? 0)} - Average{" "}
                {formatSummaryNumber(summary.average ?? 0)} - Min{" "}
                {formatSummaryNumber(summary.min ?? 0)} - Max{" "}
                {formatSummaryNumber(summary.max ?? 0)}
              </Typography>
            </Tooltip>
          )}
          {(selectionKind === "rows" || summary.numeric === 0) &&
            summary.cells > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0 }}
              >
                {summary.filled.toLocaleString()} filled cell
                {summary.filled === 1 ? "" : "s"}
                {selectionKind === "rows"
                  ? " - select cells for totals"
                  : " - none of them numeric"}
              </Typography>
            )}
          <Divider orientation="vertical" flexItem />
        </>
      )}

      {filterIds.map((columnId) => (
        <Chip
          key={columnId}
          size="small"
          sx={{ flexShrink: 0 }}
          label={`${columnTitle(source, columnId)} filtered`}
          onDelete={() => onClearFilter(columnId)}
        />
      ))}

      {state.sort.length > 0 && (
        <Chip
          size="small"
          sx={{ flexShrink: 0 }}
          label={`Sort: ${describeSort(source, state)}`}
          onDelete={onClearSort}
        />
      )}

      {state.hiddenColumns.length > 0 && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          sx={{ flexShrink: 0 }}
          label={`${state.hiddenColumns.length} column${state.hiddenColumns.length === 1 ? "" : "s"} hidden`}
          onDelete={onShowAllColumns}
        />
      )}

      {state.columnOrder !== null && (
        // No count in the label: moving one column shifts its neighbours, so
        // any number would be a number the person did not do.
        <Chip
          size="small"
          variant="outlined"
          sx={{ flexShrink: 0 }}
          label="Columns reordered"
          onDelete={onResetColumnOrder}
        />
      )}

      {frozenCount > 0 && (
        <Chip
          size="small"
          variant="outlined"
          sx={{ flexShrink: 0 }}
          label={`${frozenCount} column${frozenCount === 1 ? "" : "s"} frozen`}
          onDelete={onUnfreeze}
        />
      )}

      {Object.keys(state.columnWidths).length > 0 && (
        <Chip
          size="small"
          variant="outlined"
          sx={{ flexShrink: 0 }}
          label={`${Object.keys(state.columnWidths).length} column${Object.keys(state.columnWidths).length === 1 ? "" : "s"} resized`}
          onDelete={onResetColumnWidths}
        />
      )}

      {state.hiddenRows.length > 0 && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          sx={{ flexShrink: 0 }}
          label={`${state.hiddenRows.length.toLocaleString()} row${state.hiddenRows.length === 1 ? "" : "s"} hidden`}
          onDelete={onShowAllRows}
        />
      )}

      <Box sx={{ flex: 1, minWidth: 8 }} />

      <Typography
        variant="caption"
        color="text.secondary"
        aria-live="polite"
        sx={{ flexShrink: 0 }}
      >
        Showing {stats.visibleRows.toLocaleString()} of{" "}
        {stats.totalRows.toLocaleString()} rows
        {stats.visibleColumns !== stats.totalColumns
          ? ` - ${stats.visibleColumns} of ${stats.totalColumns} columns`
          : ""}
      </Typography>

      <Tooltip title="Undo (Ctrl+Z)">
        <span>
          <IconButton
            size="small"
            onClick={onUndo}
            disabled={!canUndo}
            aria-label="Undo"
            sx={{ flexShrink: 0 }}
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Redo (Ctrl+Shift+Z)">
        <span>
          <IconButton
            size="small"
            onClick={onRedo}
            disabled={!canRedo}
            aria-label="Redo"
            sx={{ flexShrink: 0 }}
          >
            <RedoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Put the sheet back the way it is stored">
        <Button
          size="small"
          onClick={onReset}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          Reset view
        </Button>
      </Tooltip>
    </Box>
  );
};

export default ViewStatusBar;
